import { useEffect, useMemo, useRef, useState } from 'react'
import { useFormState } from 'react-final-form'
import type { Column } from 'react-table'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Button from 'common/components/button'
import Input from 'common/components/input/Input'
import Loader from 'common/components/loaders/Loader'
import Table, { TableHandle } from 'common/components/table'
import { ArrowRight as ArrowRightIcon } from 'common/icons'

import type { BrazeCampaign } from './api/api'
import useGetCampaignDetailQuery from './api/queries/useGetCampaignDetailQuery'
import useGetCampaignsQuery from './api/queries/useGetCampaignsQuery'
import useGetCanvasDetailQuery from './api/queries/useGetCanvasDetailQuery'
import useGetCanvasesQuery from './api/queries/useGetCanvasesQuery'
import CampaignDetail from './CampaignDetail'
import MergeTags from './mergeTags'

type ImportType = 'campaigns' | 'canvas'

const PAGE_SIZE = 10

const COLUMNS: Column<BrazeCampaign>[] = [
  { Header: 'Name', accessor: 'name' },
  { Header: 'Last edited', accessor: 'last_edited', width: 180 },
]

type Props = {
  integration: string
  initialCampaignId?: string
  onSelect?: (campaign: BrazeCampaign | null) => void
  onGenerateContent: (
    id: string,
    type: ImportType,
    mergeTagIds?: string[]
  ) => void
}

const ImportTab = ({
  integration,
  initialCampaignId,
  onSelect,
  onGenerateContent,
}: Props) => {
  const { showMergeTags } = useFlags()
  const { values } = useFormState({ subscription: { values: true } })

  const [activeType, setActiveType] = useState<ImportType>('campaigns')
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState<BrazeCampaign | null>(null)
  const hasPreselected = useRef(false)
  const tableRef = useRef<TableHandle<BrazeCampaign>>(null)

  const {
    data: campaigns = [],
    isLoading: isLoadingCampaigns,
    isError: isCampaignsError,
  } = useGetCampaignsQuery({ integration })

  const {
    data: canvases = [],
    isLoading: isLoadingCanvases,
    isError: isCanvasesError,
  } = useGetCanvasesQuery({ integration })

  const { data: campaignDetail, isLoading: isLoadingCampaignDetail } =
    useGetCampaignDetailQuery({
      integration,
      campaignId: activeType === 'campaigns' ? selectedItem?.id ?? null : null,
    })

  const { data: canvasDetail, isLoading: isLoadingCanvasDetail } =
    useGetCanvasDetailQuery({
      integration,
      canvasId: activeType === 'canvas' ? selectedItem?.id ?? null : null,
    })

  const activeDetail =
    activeType === 'campaigns' ? campaignDetail : canvasDetail
  const isLoadingDetail =
    activeType === 'campaigns' ? isLoadingCampaignDetail : isLoadingCanvasDetail

  const activeItems = activeType === 'campaigns' ? campaigns : canvases
  const isLoading =
    activeType === 'campaigns' ? isLoadingCampaigns : isLoadingCanvases
  const isError =
    activeType === 'campaigns' ? isCampaignsError : isCanvasesError

  useEffect(() => {
    if (
      hasPreselected.current ||
      !initialCampaignId ||
      campaigns.length === 0
    ) {
      return
    }
    const match = campaigns.find((c) => c.id === initialCampaignId)
    if (match) {
      hasPreselected.current = true
      setSelectedItem(match)
    }
  }, [campaigns, initialCampaignId])

  const initialPageIndex = useMemo(() => {
    if (!initialCampaignId || campaigns.length === 0) {
      return 0
    }
    const matchIndex = campaigns.findIndex((c) => c.id === initialCampaignId)
    return matchIndex >= 0 ? Math.floor(matchIndex / PAGE_SIZE) : 0
  }, [campaigns, initialCampaignId])

  useEffect(() => {
    if (initialPageIndex > 0) {
      tableRef.current?.gotoPage(initialPageIndex)
    }
  }, [initialPageIndex])

  const data = useMemo(
    () =>
      activeItems.map((c) => ({ ...c, checked: c.id === selectedItem?.id })),
    [activeItems, selectedItem]
  )

  const handleTypeChange = (type: ImportType) => {
    setActiveType(type)
    setSelectedItem(null)
    setSearch('')
  }

  const handleSelect = (rows: BrazeCampaign[]) => {
    const selected = rows[0] ?? null
    if (!selected) {
      return
    }
    setSelectedItem((prev) => {
      if (prev?.id === selected.id) {
        return prev
      }
      onSelect?.(selected)
      return selected
    })
  }

  const handleGenerateContent = () => {
    if (!selectedItem) {
      return
    }
    const mergeTagIds = values.merge_tags as string[] | undefined
    onGenerateContent(selectedItem.id, activeType, mergeTagIds)
  }

  return (
    <div className="flex flex-col gap-4 pt-9">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {(['campaigns'] as ImportType[]).map((type) => (
            <button
              key={type}
              onClick={() => handleTypeChange(type)}
              className={cx(
                'px-3 py-1 text-sm font-medium rounded-full capitalize transition-colors',
                activeType === type
                  ? 'bg-maroon-400 text-white'
                  : 'text-base-500 hover:text-base-700'
              )}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[160px] max-w-[400px] ml-auto">
          <Input
            type="search"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center min-h-96">
          <Loader />
        </div>
      ) : isError || activeItems.length === 0 ? (
        <div className="flex flex-1 items-center justify-center min-h-96 text-base-500 text-sm capitalize">
          No {activeType} available.
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-1/2">
            <Table
              ref={tableRef}
              columns={COLUMNS}
              data={data}
              rowSelection="single"
              onSelectRow={handleSelect}
              onRowClick={(campaign) => handleSelect([campaign])}
              globalFilterValue={search}
              autoResetSelectedRows={false}
              autoResetGlobalFilter={false}
              autoResetPage={false}
              initialState={{
                pageSize: PAGE_SIZE,
                pageIndex: initialPageIndex,
              }}
            />
          </div>
          <div className="w-full lg:w-1/2 overflow-y-auto max-h-[816px]">
            <CampaignDetail
              detail={activeDetail}
              isLoading={!!selectedItem && isLoadingDetail}
            />
          </div>
        </div>
      )}

      {showMergeTags && <MergeTags />}

      <div className="flex justify-end pt-4">
        <Button
          variant="primary"
          disabled={!selectedItem}
          suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
          onClick={handleGenerateContent}
        >
          Generate content
        </Button>
      </div>
    </div>
  )
}

export default ImportTab
